import { dateOnlyKey, officeCalendarDate } from '@peles/shared';

/** An event whose date just moved, and the start it had before. */
export interface TaskDateShiftRequest {
  eventId: string;
  /** ISO instant of the event's previous start. */
  from: string;
}

type Listener = (request: TaskDateShiftRequest) => void;

const listeners = new Set<Listener>();

/**
 * Where a date change is announced to whoever offers to move the tasks.
 *
 * `useUpdateEvent` announces; `TaskDateShiftPrompt` (mounted once, in the app
 * shell) listens. A module-level channel rather than context, because the
 * mutation hook is used by screens that sit outside any one provider, and a
 * date change from any of them — the header's date pill, the edit form, one
 * written later — must reach the same prompt.
 */
export function onTaskDateShiftRequest(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function requestTaskDateShift(request: TaskDateShiftRequest): void {
  listeners.forEach((listener) => listener(request));
}

/**
 * Whether an event moved to another day — the office's calendar day, the one
 * its tasks are dated by. A change of time on the same day moves no task.
 */
export function eventDayChanged(
  before: Date | string | null | undefined,
  after: Date | string | null | undefined,
): boolean {
  if (!before || !after) return false;
  const key = (value: Date | string) => dateOnlyKey(officeCalendarDate(new Date(value)));
  return key(before) !== key(after);
}
